import { IUser } from '@/MODELS/user.model'

// Google Analytics gtag function declaration
declare global {
    interface Window {
        gtag: (command: 'config' | 'event' | 'set' | 'consent', targetId: string | object, parameters?: object) => void
        dataLayer: any[]
    }
}

export interface AnalyticsEvent {
    action: string
    category: string
    label?: string
    value?: number
    custom_parameters?: Record<string, any>
}

export interface UserDemographics {
    user_id?: string
    child_age_range?: string
    guardian_role?: string
    location?: string
    onboarding_complete?: boolean
    account_age_days?: number
}

class AnalyticsService {
    private isInitialized = false
    private currentUser: IUser | null = null
    private sessionStartTime: number | null = null
    private pageStartTime: number | null = null
    private chatSessionStartTime: number | null = null
    private messageCount = 0

    constructor() {
        this.init()
    }

    private init() {
        // Check if gtag is available
        if (typeof window !== 'undefined' && window.gtag) {
            this.isInitialized = true
            this.sessionStartTime = Date.now()
            this.pageStartTime = Date.now()

            // Set up page visibility API to track session duration
            document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
            window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this))
        }
    }

    private handleVisibilityChange() {
        if (document.hidden) {
            this.trackPageDuration()
        } else {
            this.pageStartTime = Date.now()
        }
    }

    private handleBeforeUnload() {
        this.trackPageDuration()
        this.trackSessionDuration()
    }

    private trackPageDuration() {
        if (this.pageStartTime) {
            const duration = Math.round((Date.now() - this.pageStartTime) / 1000)
            this.trackEvent({
                action: 'page_duration',
                category: 'engagement',
                value: duration,
                custom_parameters: {
                    page_path: window.location.pathname,
                },
            })
        }
    }

    private trackSessionDuration() {
        if (this.sessionStartTime) {
            const duration = Math.round((Date.now() - this.sessionStartTime) / 1000)
            this.trackEvent({
                action: 'session_duration',
                category: 'engagement',
                value: duration,
            })
        }
    }

    /**
     * Set user demographics for tracking (privacy-safe)
     */
    setUser(user: IUser | null) {
        if (!this.isInitialized || !user) return

        this.currentUser = user

        // Create safe demographic data
        const demographics: UserDemographics = {
            user_id: user._id,
            onboarding_complete: user.onboardingComplete || false,
        }

        // Add safe demographic info if available
        if (user.userInfo) {
            // Age range instead of exact age
            if (user.userInfo.childAge) {
                const age = parseInt(user.userInfo.childAge)
                if (!isNaN(age)) {
                    if (age <= 3) demographics.child_age_range = '0-3'
                    else if (age <= 6) demographics.child_age_range = '4-6'
                    else if (age <= 12) demographics.child_age_range = '7-12'
                    else demographics.child_age_range = '13+'
                }
            }

            // Guardian role (safe to track)
            if (user.userInfo.guardianRole && user.userInfo.guardianRole !== 'other') {
                demographics.guardian_role = user.userInfo.guardianRole
            }

            // General location (state/region level only)
            if (user.userInfo.location) {
                demographics.location = user.userInfo.location
            }
        }

        // Calculate account age
        if (user.createdAt) {
            const accountAge = Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24))
            demographics.account_age_days = accountAge
        }

        // Set user properties in GA
        window.gtag('set', demographics)
    }

    /**
     * Track custom events
     */
    trackEvent(event: AnalyticsEvent) {
        if (!this.isInitialized) return

        const eventData: any = {
            event_category: event.category,
            event_label: event.label,
            value: event.value,
            ...event.custom_parameters,
        }

        // Add user context if available
        if (this.currentUser) {
            eventData.user_id = this.currentUser._id
            eventData.onboarding_complete = this.currentUser.onboardingComplete || false
        }

        window.gtag('event', event.action, eventData)
    }

    // Page Navigation Tracking
    trackPageView(pageName: string, additionalParams?: Record<string, any>) {
        this.trackEvent({
            action: 'page_view',
            category: 'navigation',
            label: pageName,
            custom_parameters: {
                page_title: document.title,
                page_path: window.location.pathname,
                ...additionalParams,
            },
        })
        this.pageStartTime = Date.now()
    }

    // Checklist Tracking
    trackChecklistTaskToggle(taskId: string, checked: boolean, stepId: string, taskDescription?: string) {
        this.trackEvent({
            action: checked ? 'task_completed' : 'task_unchecked',
            category: 'checklist',
            label: stepId,
            custom_parameters: {
                task_id: taskId,
                step_id: stepId,
                // Don't include task description as it might contain personal info
                task_type: taskDescription ? 'described' : 'simple',
            },
        })
    }

    trackChecklistProgress(stepId: string, completedTasks: number, totalTasks: number) {
        const progressPercentage = Math.round((completedTasks / totalTasks) * 100)

        this.trackEvent({
            action: 'checklist_progress',
            category: 'checklist',
            label: stepId,
            value: progressPercentage,
            custom_parameters: {
                step_id: stepId,
                completed_tasks: completedTasks,
                total_tasks: totalTasks,
            },
        })
    }

    trackChecklistStepComplete(stepId: string, totalTasks: number, timeToComplete?: number) {
        this.trackEvent({
            action: 'step_completed',
            category: 'checklist',
            label: stepId,
            value: timeToComplete,
            custom_parameters: {
                step_id: stepId,
                total_tasks: totalTasks,
                completion_time_seconds: timeToComplete,
            },
        })
    }

    // Chat Tracking
    trackChatSessionStart(isRoadmapChat: boolean = false, roadmapId?: string) {
        this.chatSessionStartTime = Date.now()
        this.messageCount = 0

        this.trackEvent({
            action: 'chat_session_start',
            category: 'chat',
            label: isRoadmapChat ? 'roadmap_chat' : 'general_chat',
            custom_parameters: {
                chat_type: isRoadmapChat ? 'roadmap' : 'general',
                roadmap_id: roadmapId,
            },
        })
    }

    trackChatMessage(messageLength: number, isUserMessage: boolean = true, responseTime?: number) {
        if (isUserMessage) {
            this.messageCount++
        }

        this.trackEvent({
            action: isUserMessage ? 'message_sent' : 'response_received',
            category: 'chat',
            value: messageLength,
            custom_parameters: {
                message_type: isUserMessage ? 'user' : 'assistant',
                message_length: messageLength,
                session_message_count: this.messageCount,
                response_time_ms: responseTime,
            },
        })
    }

    trackChatSessionEnd() {
        if (this.chatSessionStartTime) {
            const sessionDuration = Math.round((Date.now() - this.chatSessionStartTime) / 1000)

            this.trackEvent({
                action: 'chat_session_end',
                category: 'chat',
                value: sessionDuration,
                custom_parameters: {
                    session_duration_seconds: sessionDuration,
                    total_messages: this.messageCount,
                },
            })

            this.chatSessionStartTime = null
            this.messageCount = 0
        }
    }

    trackChatAction(action: 'save' | 'delete' | 'new_chat', chatId?: string) {
        this.trackEvent({
            action: `chat_${action}`,
            category: 'chat',
            custom_parameters: {
                chat_id: chatId,
            },
        })
    }

    // Roadmap Tracking
    trackRoadmapAccess(stepId: string, stepTitle?: string, isFromDashboard: boolean = false) {
        this.trackEvent({
            action: 'roadmap_step_view',
            category: 'roadmap',
            label: stepId,
            custom_parameters: {
                step_id: stepId,
                access_point: isFromDashboard ? 'dashboard' : 'direct',
                // Don't include step title as it might contain personal info
            },
        })
    }

    trackRoadmapNavigation(fromStepId: string, toStepId: string) {
        this.trackEvent({
            action: 'roadmap_navigation',
            category: 'roadmap',
            custom_parameters: {
                from_step: fromStepId,
                to_step: toStepId,
            },
        })
    }

    trackRoadmapResourceClick(resourceId: string, resourceType: string, stepId: string) {
        this.trackEvent({
            action: 'resource_accessed',
            category: 'roadmap',
            label: resourceType,
            custom_parameters: {
                resource_id: resourceId,
                resource_type: resourceType,
                step_id: stepId,
            },
        })
    }

    // Resource Library Tracking
    trackResourceLibraryAccess() {
        this.trackEvent({
            action: 'library_access',
            category: 'resources',
        })
    }

    trackResourceSearch(searchTerm: string, resultsCount: number) {
        // Hash or truncate search term to avoid personal info
        const safeSearchTerm = searchTerm.length > 50 ? 'long_search' : 'short_search'

        this.trackEvent({
            action: 'resource_search',
            category: 'resources',
            label: safeSearchTerm,
            value: resultsCount,
            custom_parameters: {
                search_length: searchTerm.length,
                results_count: resultsCount,
            },
        })
    }

    trackResourceView(resourceId: string, resourceType: string) {
        this.trackEvent({
            action: 'resource_view',
            category: 'resources',
            label: resourceType,
            custom_parameters: {
                resource_id: resourceId,
                resource_type: resourceType,
            },
        })
    }

    trackResourceFavorite(resourceId: string, isFavoriting: boolean) {
        this.trackEvent({
            action: isFavoriting ? 'resource_favorited' : 'resource_unfavorited',
            category: 'resources',
            custom_parameters: {
                resource_id: resourceId,
            },
        })
    }

    // Onboarding Tracking
    trackOnboardingStep(stepNumber: number, stepName: string, completed: boolean = false) {
        this.trackEvent({
            action: completed ? 'onboarding_step_completed' : 'onboarding_step_viewed',
            category: 'onboarding',
            label: stepName,
            value: stepNumber,
            custom_parameters: {
                step_number: stepNumber,
                step_name: stepName,
            },
        })
    }

    trackOnboardingComplete(totalSteps: number, timeToComplete?: number) {
        this.trackEvent({
            action: 'onboarding_completed',
            category: 'onboarding',
            value: timeToComplete,
            custom_parameters: {
                total_steps: totalSteps,
                completion_time_seconds: timeToComplete,
            },
        })
    }

    // Error Tracking
    trackError(errorType: string, errorMessage: string, location: string) {
        this.trackEvent({
            action: 'error_occurred',
            category: 'errors',
            label: errorType,
            custom_parameters: {
                error_type: errorType,
                error_location: location,
                // Sanitize error message to avoid personal info
                has_error_message: !!errorMessage,
            },
        })
    }

    // Feature Usage Tracking
    trackFeatureUsage(featureName: string, action: string, value?: number) {
        this.trackEvent({
            action: `${featureName}_${action}`,
            category: 'feature_usage',
            label: featureName,
            value: value,
        })
    }
}

// Create singleton instance
export const analytics = new AnalyticsService()

// Export for use in components
export default analytics
